/**
 * The Scheduling BOARD: eMerge's own schedule, one row per building, built on
 * the board kit (components/board; docs/design/boards.md).
 *
 * Owner ruling (Isaiah Cotto, 2026-10-05): "Scheduling updates in Monday
 * should not dictate scheduling. Everything runs through eMerge including
 * scheduling." The tabs are the six eMerge states — Closed, Blocked,
 * Upcoming, Confirmed, Planned, No open plan — which add up to Main table (the
 * server's tabs drive the table and the Kanban alike; Upcoming, a confirmed
 * next visit in the next 14 days, is Isaiah Cotto's ruling of 2026-10-14); the default
 * order is Closed last, Blocked first, then the soonest planned date, then the
 * name. A building's scheduling profile (tentative visit, census, TM
 * follow-up, closure) shows as eMerge fields with their origin ("seeded from
 * Monday" for the cut-over seed). Monday's
 * columns stay visible only in a labelled, read-only "Monday reference"
 * group; they drive no tab, count, order or filter. Opening a row shows the
 * existing facility-operations panel (plan, commands, blocker, coverage,
 * history) docked beside the table, unchanged. Nothing here derives a status from time (the
 * server's Upcoming is the one named exception, decided there), and nothing here names a resident.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BoardTabs, BoardTable, BoardLegend, ColumnKey, Pager, StatusBar, StateMark, NotRecorded, SourceTag, DatedCell,
  useBoardUrlState, boardDate, boardStamp, tabSourceText, panelId, tabId,
} from '../../components/board';
import type { BoardColumn, BoardTabDef } from '../../components/board';
import { StateBlock } from '../../components/tide';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { SNF_CREDENTIALING } from '../../theme/mondayPalette';
import { schedulingApi, outcomeOf, ProviderOption, SchedulingScope } from '../finance/financeApi';
import { schedulingBoardApi, SchedulingBoardPage, SchedulingBoardRow, FilterOption, ProfileDate } from './boardApi';
import { FacilityPanelBody } from './FacilityOperations';
import { AddBuildingButton, useBuildingMeta } from './BuildingEditor';
import SchedulingKanban from './SchedulingKanban';
import { useBoardLayout } from '../../components/board/kanban/layout';

const ID_BASE = 'sc-board';
const EMERGE_GROUP = 'eMerge schedule';
const MONDAY_GROUP = 'Monday reference (read-only, not used for scheduling)';
const CLOSURE_WORDS: Record<string, string> = { DROPPED: 'dropped', DO_NOT_SCHEDULE: 'do not schedule' };

function StateCell({ r }: { r: SchedulingBoardRow }) {
  if (!r.emerge_state) {
    return r.kind === 'MONDAY_ONLY'
      ? <NotRecorded label="on Monday's board only" why="eMerge has no scheduling profile, coverage, visit plan or blocker for this building, so it holds no eMerge state. Planning a visit from the row makes it eMerge work." />
      : <NotRecorded label="not an eMerge building row" why="This Monday item names no building, so eMerge holds no state for it." />;
  }
  const s = r.emerge_state;
  // The same component the legend draws its samples with; a closed building adds its closure kind under the word.
  const closure = s.key === 'closed' && r.profile?.closure ? (CLOSURE_WORDS[r.profile.closure.kind] || r.profile.closure.kind) : null;
  return <StateMark word={s.word} color={s.color} marker={s.marker} stateKey={s.key} sub={closure} />;
}

/** A profile date: an eMerge field, labelled with where it came from. */
const profileDate = (r: SchedulingBoardRow, d: ProfileDate | null | undefined, tentative = false) => {
  if (!r.profile) return <NotRecorded label="no scheduling profile" />;
  return <DatedCell on={boardDate(d?.on)} source="EMERGE" what={d?.origin_text} tentative={tentative} />;
};

const mondayNone = <NotRecorded label="no scheduling row" why="Monday holds no scheduling item for this building." />;
const mondayDate = (r: SchedulingBoardRow, v: string | null | undefined, tentative = false) =>
  (!r.monday ? mondayNone : <DatedCell on={boardDate(v)} source="MONDAY" tentative={tentative} />);

function columnsFor(): BoardColumn<SchedulingBoardRow>[] {
  return [
    {
      key: 'state', label: 'eMerge state', sort: 'state', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The building\'s one eMerge state, as a word and a shape: Closed (its scheduling profile is closed: dropped, or do not schedule), else Blocked (an open blocker, whatever else is planned), else the state of the next open visit plan: Upcoming (confirmed and dated today through today + 14, America/New_York days -- the one state that reads the calendar, Isaiah Cotto ruling of 2026-10-14, decided by the server), Confirmed (any other confirmed date, including one that has passed) or Planned, else No open plan. Nothing else is derived from dates.',
      render: (r) => <StateCell r={r} />,
    },
    {
      key: 'next-visit', label: 'Next visit', sort: 'next-visit', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The next open eMerge visit plan (planned or confirmed) and the provider on it. A date that has passed stays as Scheduling left it.',
      render: (r) => {
        if (!r.next_visit) return r.kind !== 'UNLINKED' ? <NotRecorded label="no open plan" /> : <NotRecorded />;
        const v = r.next_visit;
        return (
          <span className="board-dated">
            <span className="board-dated__value">{boardDate(v.on)}</span>
            <span>{v.provider ? (v.provider.name || 'Provider name not recorded') : <NotRecorded label="no provider on the plan" />}</span>
            <SourceTag source="EMERGE" detail={v.what} />
          </span>
        );
      },
    },
    {
      key: 'last-visit', label: 'Last visit', sort: 'last-visit', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The later of two eMerge facts, labelled with which one: a provider check-in, or a visit plan Scheduling marked completed. Scheduling\'s record, not proof of a test. The Monday date is in the Monday reference, never here.',
      render: (r) => <DatedCell on={boardDate(r.last_visit.value?.on)} source="EMERGE" what={r.last_visit.value?.what} none={<NotRecorded label="none recorded in eMerge" />} />,
    },
    {
      key: 'tentative', label: 'Tentative visit', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The building\'s tentative next visit on its eMerge scheduling profile. Tentative: nobody has planned it, so it never makes the building Planned. "seeded from Monday" means the cut-over seed copied it; it is eMerge\'s value now.',
      render: (r) => profileDate(r, r.profile?.tentative_next_visit, true),
    },
    {
      key: 'census', label: 'Census', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The census date on the building\'s eMerge scheduling profile, with where it came from.',
      render: (r) => profileDate(r, r.profile?.census),
    },
    {
      key: 'tm', label: 'TM follow-up', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The TM follow-up visit date on the building\'s eMerge scheduling profile, with where it came from.',
      render: (r) => profileDate(r, r.profile?.tm_follow_up),
    },
    {
      key: 'coverage', label: 'Coverage', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The providers eMerge\'s coverage record has ACTIVE on this building (provider_facility_assignments). Proposals, including the ones read from Monday, are decided in the panel and are not shown here.',
      render: (r) => (r.coverage.length
        ? <span className="board-dated"><span>{r.coverage.map((c) => `${c.name || 'Name not recorded'}${c.coverage_type && c.coverage_type !== 'PRIMARY' ? ` (${c.coverage_type.toLowerCase()})` : ''}`).join(', ')}</span><SourceTag source="EMERGE" detail="active coverage" /></span>
        : <NotRecorded label="no active coverage" />),
    },
    {
      key: 'blocker', label: 'Blocker', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'An open blocker Scheduling recorded in eMerge on why the building cannot be scheduled.',
      render: (r) => (r.blocker ? <><span className="board-clip" title={r.blocker.reason}>{r.blocker.reason}</span><SourceTag source="EMERGE" detail={`since ${boardDate(r.blocker.recorded_at) || 'an unrecorded date'}`} /></> : <span className="board-na">none</span>),
    },
    {
      key: 'manifest', label: 'Manifest link', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The building\'s manifest link as eMerge holds it (kept by owner ruling, 2026-10-05). Set or clear it in the row\'s panel. Where eMerge holds none yet, Monday\'s link is shown as a labelled reference; a Monday sync never changes the eMerge link.',
      render: (r) => <ManifestLinkCell r={r} />,
    },
    {
      key: 'credentialing', label: 'Credentialing', source: 'EMERGE', group: EMERGE_GROUP,
      help: 'The building\'s credentialing word. eMerge has been its authority since the 2026-10-05 ruling; the word was adopted from Monday, and Monday no longer changes it. Shown, enforces nothing (CRED-RULING-001).',
      render: (r) => (r.credentialing ? <><FilledStatusChip word={r.credentialing.word} column={SNF_CREDENTIALING} size="sm" /><SourceTag source="EMERGE" detail="not enforced" /></> : <NotRecorded />),
    },
    {
      key: 'm-status', label: 'Status word', source: 'MONDAY', group: MONDAY_GROUP,
      help: 'Monday\'s SCHEDULING word in Monday\'s colour (a bar beside the word). Shown for reference; it decides nothing here.',
      render: (r) => (!r.monday ? mondayNone : (
        <span className="board-dated">
          <StatusBar word={r.monday.status?.word} color={r.monday.status?.color ?? null} />
          <SourceTag source="MONDAY" detail={r.monday.items > 1 ? `newest of ${r.monday.items} items` : undefined} />
        </span>
      )),
    },
    {
      key: 'm-provider', label: 'Board provider', source: 'MONDAY', group: MONDAY_GROUP,
      help: 'The Monday item\'s Assigned Provider (a Monday user, not an eMerge provider). Reference only; the provider filter uses eMerge coverage and plans.',
      render: (r) => (!r.monday ? mondayNone : r.monday.provider ? <>{r.monday.provider.name || 'Name not recorded'}<SourceTag source="MONDAY" /></> : <NotRecorded />),
    },
    { key: 'm-last', label: 'Board last visit', source: 'MONDAY', group: MONDAY_GROUP, help: 'Monday\'s Date of Last Visit. Monday derives it from the planned date, so it is not proof of a visit.', render: (r) => mondayDate(r, r.monday?.last_visit) },
    { key: 'm-next', label: 'Board next visit', source: 'MONDAY', group: MONDAY_GROUP, help: 'Monday\'s Provider Next Visit date. Reference only; the eMerge Next visit is the plan.', render: (r) => mondayDate(r, r.monday?.next_visit) },
    { key: 'm-tentative', label: 'Board tentative visit', source: 'MONDAY', group: MONDAY_GROUP, help: 'Monday\'s Tentative Next Visit. Tentative: nobody has scheduled it.', render: (r) => mondayDate(r, r.monday?.tentative_next_visit, true) },
    { key: 'm-census', label: 'Board census date', source: 'MONDAY', group: MONDAY_GROUP, help: 'Monday\'s CENSUS DATE, as Monday has it today.', render: (r) => mondayDate(r, r.monday?.census_date) },
    { key: 'm-tm', label: 'Board TM follow-up', source: 'MONDAY', group: MONDAY_GROUP, help: 'Monday\'s TM FOLLOW UP VISIT date, as Monday has it today.', render: (r) => mondayDate(r, r.monday?.tm_follow_up) },
    {
      key: 'm-residents', label: 'Residents on the item', numeric: true, source: 'MONDAY', group: MONDAY_GROUP,
      help: 'How many residents Monday lists under the item (its subitems). A count only; Monday\'s list, not an eMerge visit roster.',
      render: (r) => (!r.monday ? mondayNone : <><span className="board-money">{r.monday.residents_on_item.toLocaleString('en-US')}</span><SourceTag source="MONDAY" /></>),
    },
    {
      key: 'm-comments', label: 'Comments', source: 'MONDAY', group: MONDAY_GROUP,
      help: 'Monday\'s Comments, cut short here; the whole text is in the open row.',
      render: (r) => (!r.monday ? mondayNone : r.monday.comments ? <><span className="board-clip" title={r.monday.comments}>{r.monday.comments}</span><SourceTag source="MONDAY" /></> : <NotRecorded />),
    },
  ];
}

/** The building's eMerge scheduling profile, in the open row: status, closure and where its fields came from. */
function ProfileFacts({ r }: { r: SchedulingBoardRow }) {
  const p = r.profile;
  if (!p) return null;
  return (
    <dl className="board-detail__facts" aria-label="eMerge scheduling profile">
      <div><dt>On eMerge&rsquo;s schedule</dt><dd>{p.status === 'CLOSED' ? 'Closed' : 'Active'} <SourceTag source="EMERGE" detail={p.origin_text} /></dd></div>
      {p.closure && <div><dt>Closed</dt><dd>{CLOSURE_WORDS[p.closure.kind] || p.closure.kind}{p.closure.reason ? ` — ${p.closure.reason}` : ''}</dd></div>}
    </dl>
  );
}

/** The board cell: eMerge's link; otherwise Monday's, labelled as a reference only. */
function ManifestLinkCell({ r }: { r: SchedulingBoardRow }) {
  if (r.manifest_link) {
    return (
      <span className="board-dated">
        <a href={r.manifest_link.url} target="_blank" rel="noreferrer noopener">manifest link ↗</a>
        <SourceTag source="EMERGE" detail={r.manifest_link.origin === 'MONDAY_IMPORT' ? 'copied once from Monday' : 'set in eMerge'} />
      </span>
    );
  }
  if (r.manifest_link_state === 'CLEARED') return <NotRecorded label="no link (cleared in eMerge)" />;
  if (r.monday_manifest_link) {
    return (
      <span className="board-dated">
        <span className="board-na">not set in eMerge</span>{' '}
        <a href={r.monday_manifest_link.url} target="_blank" rel="noreferrer noopener">Monday reference ↗</a>
        <SourceTag source="MONDAY" detail="reference only" />
      </span>
    );
  }
  return <NotRecorded label="no manifest link" />;
}

/**
 * The building's manifest link in the row panel: what eMerge holds, Monday's
 * as a reference, and (for a Scheduling manager) the one place to set it.
 */
function ManifestLinkFacts({ r, canEdit, onChanged }: { r: SchedulingBoardRow; canEdit: boolean; onChanged: (msg: string) => Promise<void> }) {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (!r.facility) return null;
  const facilityId = r.facility.id;
  const save = async (url: string | null) => {
    setBusy(true); setProblem(null);
    try {
      const res = await schedulingBoardApi.setManifestLink(facilityId, url, r.manifest_link_version ?? 0);
      if (res.success) {
        setEditing(false); setDraft('');
        await onChanged(url ? `The manifest link for ${r.facility?.name || 'the building'} is set in eMerge.` : `${r.facility?.name || 'The building'} now has no manifest link in eMerge.`);
      } else setProblem(outcomeOf(res));
    } catch { setProblem('Could not reach the server; the link was not changed.'); } finally { setBusy(false); }
  };
  return (
    <dl className="board-detail__facts" aria-label="Manifest link">
      <div>
        <dt>Manifest link</dt>
        <dd>
          {r.manifest_link
            ? <><a href={r.manifest_link.url} target="_blank" rel="noreferrer noopener">Open the workbook ↗</a> <SourceTag source="EMERGE" detail={`${r.manifest_link.origin_text}${r.manifest_link.set_by ? ` · ${r.manifest_link.set_by}` : ''}${r.manifest_link.set_at ? ` · ${boardDate(r.manifest_link.set_at)}` : ''}`} /></>
            : <NotRecorded label={r.manifest_link_state === 'CLEARED' ? 'none: cleared in eMerge' : 'not set in eMerge'} />}
        </dd>
      </div>
      {r.monday_manifest_link && (
        <div>
          <dt>Monday reference</dt>
          <dd><a href={r.monday_manifest_link.url} target="_blank" rel="noreferrer noopener">Monday&rsquo;s link ↗</a> <SourceTag source="MONDAY" detail="reference only; not the link" /></dd>
        </div>
      )}
      {canEdit && (
        <div>
          <dt>Change</dt>
          <dd>
            {!editing && (
              <>
                <button type="button" className="board-btn" onClick={() => { setEditing(true); setDraft(r.manifest_link?.url || ''); setProblem(null); }}>{r.manifest_link ? 'Change the link' : 'Set the link'}</button>
                {r.monday_manifest_link && !r.manifest_link && r.manifest_link_state !== 'CLEARED' && (
                  <button type="button" className="board-btn" disabled={busy} onClick={() => save(r.monday_manifest_link!.url)}>Keep Monday&rsquo;s link in eMerge</button>
                )}
                {r.manifest_link && <button type="button" className="board-btn" disabled={busy} onClick={() => save(null)}>Clear: this building has no link</button>}
              </>
            )}
            {editing && (
              <form onSubmit={(e) => { e.preventDefault(); save(draft.trim()); }}>
                <label className="board-field">
                  <span>Workbook link (https://)</span>
                  <input type="url" value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={2048} aria-label="Workbook link" />
                </label>
                <button type="submit" className="board-btn" disabled={busy || !draft.trim()}>Save</button>
                <button type="button" className="board-btn" disabled={busy} onClick={() => { setEditing(false); setProblem(null); }}>Cancel</button>
              </form>
            )}
            {problem && <p className="board-basis" role="alert">{problem}</p>}
          </dd>
        </div>
      )}
    </dl>
  );
}

function MondayFacts({ r }: { r: SchedulingBoardRow }) {
  if (!r.monday) return <p className="board-basis" style={{ padding: '8px 16px' }}>Monday holds no scheduling item for this building. Everything below is eMerge&rsquo;s.</p>;
  const m = r.monday;
  return (
    <dl className="board-detail__facts" aria-label={MONDAY_GROUP}>
      <div><dt>Monday reference</dt><dd>Read-only, not used for scheduling{m.items > 1 ? ` · newest of ${m.items} items` : ''}</dd></div>
      <div><dt>Status word</dt><dd>{m.status?.word || 'No status'} <SourceTag source="MONDAY" /></dd></div>
      <div><dt>Residents on the item</dt><dd>{m.residents_on_item} <SourceTag source="MONDAY" detail="subitems" /></dd></div>
      <div><dt>Tentative next visit</dt><dd>{boardDate(m.tentative_next_visit) ? <>{boardDate(m.tentative_next_visit)} <span className="board-tentative">tentative</span></> : <NotRecorded />}</dd></div>
      <div><dt>Comments</dt><dd>{m.comments || <NotRecorded />}</dd></div>
      <div><dt>Written by the Monday sync</dt><dd>{boardStamp(m.updated_at) || <NotRecorded />}</dd></div>
    </dl>
  );
}

function FilterSelect({ label, value, options, onChange, allLabel }: { label: string; value: string; options: FilterOption[]; onChange: (v: string) => void; allLabel: string }) {
  return (
    <label className="board-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">{allLabel}</option>
        {value && !options.some((o) => o.id === value) && <option value={value}>Selected (not in this scope)</option>}
        {options.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.count.toLocaleString('en-US')})</option>)}
      </select>
    </label>
  );
}

/** What the tabs are, and the proof that the four states make Main table. */
function StatesBasis({ page }: { page: SchedulingBoardPage }) {
  const states = page.tabs.filter((t) => t.bucket);
  const main = page.tabs.find((t) => t.key === 'all');
  const sum = states.reduce((a, t) => a + (t.count || 0), 0);
  const fmt = (n: number | undefined) => (n || 0).toLocaleString('en-US');
  return (
    <>
      <p className="board-basis">
        Tabs are eMerge scheduling states, not Monday. Every building is in exactly one:{' '}
        {states.map((t, i) => <React.Fragment key={t.key}>{i ? ' + ' : ''}{t.label} {fmt(t.count)}</React.Fragment>)} = Main table {fmt(main?.count)}.
        {' '}Buildings only Monday lists are kept apart on a reference tab.
      </p>
      {main && sum !== main.count && <p className="board-caveat" role="note">The state counts do not add up to Main table ({fmt(sum)} vs {fmt(main.count)}). Tell engineering; do not rely on these counts.</p>}
    </>
  );
}

interface LoadError { message: string; status?: number; code?: string }

function SchedulingTableBoard({ scope, onNotice, onOpenFacilities }: { scope: SchedulingScope; onNotice: (msg: string) => void; onOpenFacilities?: () => void }) {
  const [st, update] = useBoardUrlState(['provider', 'building']);
  const [data, setData] = useState<SchedulingBoardPage | null>(null);
  const [lastTabs, setLastTabs] = useState<BoardTabDef[]>([]);
  const [lastOptions, setLastOptions] = useState<SchedulingBoardPage['filter_options'] | null>(null);
  const [err, setErr] = useState<LoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(st.q);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const buildingMeta = useBuildingMeta();
  const seq = useRef(0);
  useEffect(() => { setDraft(st.q); }, [st.q]);
  // A link that names a tab lands on that tab, always. Only an unnamed landing may move.
  const [sp] = useSearchParams();
  const landing = useRef(!sp.has('tab'));
  const [landedOnMonday, setLandedOnMonday] = useState(false);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    try {
      const r = await schedulingBoardApi.board({ tab: st.tab, q: st.q, provider: st.filters.provider, building: st.filters.building, sort: st.sort, dir: st.dir, page: st.page });
      if (mine !== seq.current) return;
      if (r.success && r.data) {
        setData(r.data); setLastTabs(r.data.tabs); setLastOptions(r.data.filter_options);
      } else {
        // Never an empty board on a failure: the rows are dropped and the failure is said.
        setData(null);
        setErr({ message: r.code === 'TAB_UNKNOWN' ? 'That view does not exist on this board. The tabs are eMerge states now; Monday status and group views were retired.' : outcomeOf(r), status: r.status, code: r.code });
      }
    } catch {
      if (mine === seq.current) { setData(null); setErr({ message: 'Could not reach the server, so nothing here is current.' }); }
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [st.tab, st.q, st.filters.provider, st.filters.building, st.sort, st.dir, st.page]);
  useEffect(() => { load(); }, [load]);

  /*
   * While eMerge holds no scheduling for any building in scope (no visit plan,
   * blocker or confirmed coverage), Main table is empty and every building is on
   * Monday's board only. Landing there with no tab named, the board moves once to
   * that reference tab and says why, so nobody opens to an empty page. It moves
   * once, never on a named link, never with a search or filter, and never back.
   */
  useEffect(() => {
    if (!landing.current || !data || data.tab !== 'all') return;
    landing.current = false;
    if (st.q || st.filters.provider || st.filters.building) return;
    const mondayOnly = data.tabs.find((t) => t.key === 'monday-only');
    if (data.total === 0 && mondayOnly && (mondayOnly.count || 0) > 0) {
      setLandedOnMonday(true);
      update({ tab: 'monday-only', sort: null });
    }
  }, [data, st.q, st.filters.provider, st.filters.building, update]);
  const mondayOnlyCount = (data ? data.tabs : lastTabs).find((t) => t.key === 'monday-only')?.count || 0;
  const mainCount = data ? data.tabs.find((t) => t.key === 'all')?.count : undefined;

  const canPlan = Boolean(data ? data.can.plan : scope.can.plan);
  useEffect(() => {
    if (!canPlan) return;
    (async () => { try { const r = await schedulingApi.providers(); if (r.success && r.data) setProviders(r.data); } catch { /* the picker is empty; the server still refuses a bad id */ } })();
  }, [canPlan]);

  const columns = useMemo(() => columnsFor(), []);
  const tabs = data ? data.tabs : lastTabs.map((t) => ({ ...t, count: undefined }));
  const activeTab = tabs.find((t) => t.key === st.tab) || null;
  const options = data ? data.filter_options : lastOptions;
  const filtered = Boolean(st.q || st.filters.provider || st.filters.building);
  const onChanged = async (msg: string) => { onNotice(msg); await load(); };
  // A building added in eMerge is eMerge work at once: it lands on Main table under "No open plan". Show it there.
  const onAdded = (b: { name: string }) => {
    onNotice(`${b.name} added. It is on Main table under "No open plan"; open it to plan a visit or assign coverage.`);
    setDraft(b.name);
    update({ tab: 'no-plan', q: b.name, filters: { provider: '', building: '' }, sort: null });
  };
  const noun = (n: number) => (st.tab === 'unlinked' ? (n === 1 ? 'Monday item' : 'Monday items') : (n === 1 ? 'building' : 'buildings'));
  // MONDAY_ONLY rows keep their panel: planning a visit there is how a building becomes eMerge work.

  const identity = {
    label: 'Building', sort: 'facility',
    help: 'One row per building. Open a row for its Monday reference and, underneath, the visit plan, commands, blocker and coverage.',
    render: (r: SchedulingBoardRow) => (
      <>
        <strong>{r.facility?.name || 'Not linked to a building'}</strong>
        <span className="board-src">{r.facility ? [r.facility.city, r.facility.region].filter(Boolean).join(' · ') : 'Monday item'}</span>
        {r.facility?.origin && <span className="board-src board-origin" title="Provenance only. eMerge is the authority for this building.">{r.facility.origin.text}</span>}
      </>
    ),
    name: (r: SchedulingBoardRow) => r.facility?.name || 'this row',
    openable: (r: SchedulingBoardRow) => Boolean(r.facility),
    closedWhy: () => 'No building is linked to this Monday item, so nothing can be planned from it.',
  };

  return (
    <section className="board board-frame" aria-label="Scheduling board">
      {tabs.length > 0 && <BoardTabs tabs={tabs} value={st.tab} onChange={(k) => update({ tab: k, sort: null })} label="Scheduling board views" idBase={ID_BASE} />}
      <div role="tabpanel" id={panelId(ID_BASE)} aria-labelledby={activeTab ? tabId(ID_BASE, activeTab.key) : undefined} className="board-panel">
        <div className="board-head">
          {activeTab && (
            <p className="board-what">
              {(data && data.what) || activeTab.what}
              <span className="board-what__src">{tabSourceText(activeTab)}</span>
            </p>
          )}
          {data && <StatesBasis page={data} />}
          {landedOnMonday && st.tab === 'monday-only' && mainCount === 0 && (
            <p className="board-caveat" role="note">
              Opened here because eMerge holds no scheduling for any building in your scope yet, so Main table is empty.
              Every building below is on Monday&rsquo;s board only. Open a row to plan a visit or confirm coverage, and that building moves into Main table.
            </p>
          )}
          <div className="board-controls">
            <form className="board-search" role="search" onSubmit={(e) => { e.preventDefault(); update({ q: draft.trim() }); }}>
              <label className="board-field"><span>Building</span>
                <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Search by building name" aria-label="Search buildings" />
              </label>
              <button type="submit" className="board-btn">Find</button>
            </form>
            <FilterSelect label="eMerge provider" value={st.filters.provider} options={options?.providers || []} allLabel="Every provider" onChange={(v) => update({ filters: { provider: v } })} />
            <FilterSelect label="Building" value={st.filters.building} options={options?.buildings || []} allLabel="Every building" onChange={(v) => update({ filters: { building: v } })} />
            {filtered && <button type="button" className="board-btn" onClick={() => { setDraft(''); update({ q: '', filters: { provider: '', building: '' } }); }}>Clear search and filters</button>}
            <AddBuildingButton meta={buildingMeta} onAdded={onAdded} />
            {data && (
              <span className="board-result" aria-live="polite">
                <strong>{data.total.toLocaleString('en-US')}</strong> {noun(data.total)} in {activeTab?.label || 'this view'}{filtered ? ' matching your search and filters' : ''}{loading ? ' · updating…' : ''}
              </span>
            )}
          </div>
          {data && (
            <BoardLegend
              title="eMerge states"
              tabs={data.tabs.filter((t) => t.bucket)}
              explain
              units={['building', 'buildings']}
              value={st.tab}
              onSelect={(k) => update({ tab: k, sort: null })}
              scope="Each building is in exactly one state, shown by a word, a shape and a colour; the state counts add up to Main table and follow your search and filters. Only Upcoming reads a date: a confirmed visit in the next 14 days, decided by the server. Nothing here is derived from Monday."
            />
          )}
        </div>

        {!data && loading && !err && <div className="board-state"><StateBlock kind="loading" shape="table" label="Loading the Scheduling board…" /></div>}
        {err && (
          <div className="board-state">
            <StateBlock kind="error" status={err.status} message={err.message} onRetry={load} />
            {err.code === 'TAB_UNKNOWN' && <p style={{ marginTop: 8 }}><button type="button" className="board-btn" onClick={() => update({ tab: 'all', sort: null })}>Open the main table</button></p>}
          </div>
        )}
        {data && data.items.length === 0 && data.total > 0 && (
          <div className="board-state">
            <StateBlock kind="empty" reason="filtered" message={`Page ${st.page} is past the end: this view has ${data.total.toLocaleString('en-US')} ${noun(data.total)}.`} onClear={() => update({ page: 1 })} clearLabel="Go to page 1" />
          </div>
        )}
        {data && data.items.length === 0 && data.total === 0 && (
          <div className="board-state">
            {filtered
              ? <StateBlock kind="empty" reason="filtered" message={`No rows in ${activeTab?.label || 'this view'} match your search and filters.`} onClear={() => { setDraft(''); update({ q: '', filters: { provider: '', building: '' } }); }} clearLabel="Clear search and filters" />
              : st.tab === 'all' && mondayOnlyCount > 0
                ? <StateBlock kind="empty" reason="filtered" message={`eMerge holds no scheduling for any building in your scope yet: no scheduling profile, visit plan, blocker or confirmed coverage. ${mondayOnlyCount.toLocaleString('en-US')} ${mondayOnlyCount === 1 ? 'building is' : 'buildings are'} on Monday's board only.`} onClear={() => update({ tab: 'monday-only', sort: null })} clearLabel="Show the buildings on Monday's board" />
                : <StateBlock kind="empty" reason="none" message={`Nothing is in ${activeTab?.label || 'this view'} right now.`} />}
          </div>
        )}
        {data && data.items.length > 0 && (
          <BoardTable<SchedulingBoardRow>
            label={`Scheduling board — ${activeTab?.label || data.tab}, ${data.total} ${noun(data.total)}`}
            columns={columns}
            rows={data.items}
            rowKey={(r) => r.id}
            identity={identity}
            openKey={st.open}
            onToggle={(k) => update({ open: st.open === k ? null : k })}
            detailLabel={(r) => `${r.facility?.name || 'Row'}: operations and Monday reference`}
            renderDetail={(r) => (
              <>
                <ProfileFacts r={r} />
                <ManifestLinkFacts r={r} canEdit={canPlan} onChanged={onChanged} />
                <MondayFacts r={r} />
                {r.facility && <FacilityPanelBody facilityId={r.facility.id} name={r.facility.name || 'This building'} row={null} canPlan={canPlan} providers={providers} onChanged={onChanged} buildingMeta={buildingMeta} />}
              </>
            )}
            sort={data.sort} dir={data.dir} sorts={data.sorts}
            onSort={(col, dir) => update({ sort: col, dir })}
            busy={loading}
            rowClass={(r) => (r.blocker ? 'is-blocked' : '')}
          />
        )}
        {data && <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => update({ page: p })} />}
      </div>
      <div className="board-foot">
        {data && activeTab && (
          <p>
            Counted and listed by one predicate ({activeTab.predicate}); the count on the tab is the number of rows here.
            {' '}&ldquo;not recorded&rdquo; means the source holds no value, never that a column failed.
          </p>
        )}
        <p>
          Monday reference: the Monday sync last wrote the scheduling table {boardStamp(data?.synced_at) || 'at an unrecorded time'}. It is shown for reference only and does not decide any tab, count, order or filter.
          {' '}Not mirrored, so not shown: {(data?.not_mirrored || ['SSD EMAIL SENT', 'CONFIRMED VISIT']).join(' and ')} are Monday columns the mirror does not carry. eMerge does not invent them.
        </p>
        {onOpenFacilities && <p>A building that is not on the board? <button type="button" className="board-btn" onClick={onOpenFacilities}>Open the facility table (every building)</button>{buildingMeta?.can.create ? ' or add it here with "Add a building".' : ''}</p>}
        {data && data.items.some((r) => r.facility?.origin) && <p>Every building&rsquo;s details are eMerge&rsquo;s: a building that came from Monday says &ldquo;origin: Monday item …&rdquo;, which is where it came from, not who changes it. Monday no longer changes a building&rsquo;s details.</p>}
        <ColumnKey columns={columns} identity={identity} notes={[`Columns under "${MONDAY_GROUP}" come from the building's newest Monday scheduling row; board provider names come from Monday user records (monday_users).`]} />
      </div>
    </section>
  );
}

/**
 * Kanban | Table. The Kanban is the default (Isaiah Cotto, 2026-10-14); `?layout=table` is the table, and a
 * `?layout=kanban` link still works (boardLayoutOf). The workspace sidebar's Views group switches between them
 * (SchedulingInbox). Either layout reads GET /scheduling/board.
 */
export default function SchedulingBoard(props: { scope: SchedulingScope; onNotice: (msg: string) => void; onOpenFacilities?: () => void }) {
  const layout = useBoardLayout();
  if (layout === 'kanban') return <SchedulingKanban scope={props.scope} onNotice={props.onNotice} />;
  return <SchedulingTableBoard {...props} />;
}
